import React, { useEffect, useState, useMemo } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useDispatch } from "react-redux";
import { useGlobalContext } from "../context/global";
import {
  SIDEBAR_MENU_CONFIG,
  getActiveMainId,
  getActiveSubId,
  getNewBadgeLabel,
  menuHasNewBadge,
  withSavedReportsInMenu,
} from "../constant/sidebarMenuConfig";
import { dashboardActions } from "../redux/slices/dashboardSlice";
import "./sidebar.css";
import { getClientDataFromAdyogiAPI } from "../helper/clientData/utils";
import useUserDetails from "../helper/userData/useUserDetails";

import SettingsRounded from "@mui/icons-material/SettingsRounded";
import ChevronLeft from "@mui/icons-material/ChevronLeft";
import ChevronRight from "@mui/icons-material/ChevronRight";
import MoreVert from "@mui/icons-material/MoreVert";
import LockOutlined from "@mui/icons-material/LockOutlined";
import { IconButton, Menu, MenuItem, Tooltip } from "@mui/material";
import { deleteCustomReportById } from "./Reports/Components/utils";

function SidebarNewBadge({ variant = "subpanel", label = "New" }) {
  return (
    <span
      className={`sidebar-new-badge sidebar-new-badge--${variant}`}
      aria-label={label}
    >
      {label}
    </span>
  );
}

function SubMenuItemLabel({ name, isNew, newLabel }) {
  return (
    <span className="sidebar-subpanel-item-content">
      <span className="sidebar-subpanel-item-name">{name}</span>
      {isNew ? (
        <SidebarNewBadge variant="subpanel" label={newLabel} />
      ) : null}
    </span>
  );
}

function Sidebar() {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const [userDetails] = useUserDetails();
  const {
    config,
    selectedPlatform,
    clientId,
    clientData,
    customSavedReports,
    refreshCustomSavedReports,
  } = useGlobalContext();
  const isMarketPlaceHOBID = clientData?.admin_info?.marketplace_hob_id ?? false;
  const isMultiuser = userDetails?.user?.multiUser === "yes";


  const platform = selectedPlatform?.toLowerCase()?.includes("amazon")
    ? "amazon"
    : selectedPlatform === "flipkart"
    ? "flipkart"
    : "myntra";

    const navigateToMarketplaceBI = () => {
      navigate(
        `./reports?title=Marketplace+BI+Dashboard&url=${encodeURIComponent(
          "https://lookerstudio.google.com/embed/reporting/8a377d3b-a12c-4cb9-b349-9a2662842943/page/p_qp477rd3jd"
        )}`
      );
    };
  
    const navigateToAFMReport = () => {
      const encodedHobId = encodeURIComponent(isMarketPlaceHOBID);
      const reportUrl = `https://lookerstudio.google.com/embed/reporting/c6945a76-1f8b-4ca6-b267-611496bc174b/page/p_2cteipwopd?params=%7B%22ds1.hob_id%22%3A%22${encodedHobId}%22%7D`;
      navigate(
        `./reports?title=AFM+Report&url=${encodeURIComponent(reportUrl)}`
      );
    };

    const handleExternalAction = async (action) => {
      switch (action) {
        case "OPEN_ADMIN": {
          const email = userDetails?.user?.email;
          const token = userDetails?.token;
          if (email && token) {
            window.open(
              `https://app2.adyogi.com/amazon-admin?platformMarker=react&userEmail=${email}&ref=${token}`
            );
          }
          break;
        }
    
        case "OPEN_CMS": {
          try {
            const response = await getClientDataFromAdyogiAPI(clientId);
            const url = response?.data?.results?.[0]?.amazon_setup?.cms;
            if (url) window.open(url);
          } catch (e) {
            console.error(e);
          }
          break;
        }
    
        case "MARKETPLACE_BI": {
          navigateToMarketplaceBI();
          break;
        }
    
        case "AFM_REPORT": {
          navigateToAFMReport();
          break;
        }
    
        default:
          break;
      }
    };
    

  const savedReportMenuItems = useMemo(() => {
    return (customSavedReports ?? []).map((report) => {
      const reportId = report?.id ?? report?._id ?? report?.key;
      const pageURL = report?.pageURL;
      const cubeFilters = report?.cubeFilters;
      const encodedFilters = encodeURIComponent(
        JSON.stringify(cubeFilters ?? {})
      );
      const separator = pageURL?.includes("?") ? "&" : "?";
      const reportLink = pageURL
        ? `${pageURL}${separator}urlQuery=${encodedFilters}&savedReportId=${encodeURIComponent(
            reportId
          )}`
        : "";
      return {
        id: `saved_report_${reportId}`,
        name: report?.reportName || report?.name || "Saved Report",
        link: reportLink,
        reportId,
        reportKey: report?.key,
        pageURL,
      };
    }).filter((reportItem) => !!reportItem?.link);
  }, [customSavedReports]);

  const performanceSavedReportItems = useMemo(
    () =>
      savedReportMenuItems.filter((item) =>
        item?.pageURL?.includes("performance-analysis")
      ),
    [savedReportMenuItems]
  );

  const productAnalyticsSavedReportItems = useMemo(
    () =>
      savedReportMenuItems.filter((item) =>
        item?.pageURL?.includes("product-analytics")
      ),
    [savedReportMenuItems]
  );

  const menuConfig = useMemo(() => {
    const baseMenuConfig =
      SIDEBAR_MENU_CONFIG[platform] || SIDEBAR_MENU_CONFIG.amazon;
    return withSavedReportsInMenu(baseMenuConfig, {
      performanceSavedReportItems,
      productAnalyticsSavedReportItems,
    });
  }, [platform, performanceSavedReportItems, productAnalyticsSavedReportItems]);

  const [openSubPanelId, setOpenSubPanelId] = useState(null);
  const [savedReportMenuAnchorEl, setSavedReportMenuAnchorEl] = useState(null);
  const [selectedSavedReportForAction, setSelectedSavedReportForAction] =
    useState(null);

  const activeMainId = useMemo(
    () => getActiveMainId(location.pathname, menuConfig, location.search),
    [location.pathname, location.search, menuConfig]
  );

  const activeSubId = useMemo(
    () => getActiveSubId(location.pathname, menuConfig, location.search),
    [location.pathname, location.search, menuConfig]
  );

  useEffect(() => {
    const activeMain = menuConfig.find((m) => m.id === activeMainId);
    const hasSub = activeMain?.subMenu?.categories?.length > 0;
    if (activeMainId && hasSub) {
      setOpenSubPanelId(activeMainId);
    } else {
      setOpenSubPanelId(null);
    }
  }, [activeMainId, menuConfig]);

  useEffect(() => {
    if (openSubPanelId != null) {
      dispatch(dashboardActions.dashboardSideBarVisibiltyStateShow());
    } else {
      dispatch(dashboardActions.dashboardSideBarVisibiltyStateHide());
    }
  }, [openSubPanelId, dispatch]);

  const selectedMain = menuConfig.find((m) => m.id === openSubPanelId);
  const hasSubPanel = selectedMain?.subMenu?.categories?.length > 0;
  const activeMain = menuConfig.find((m) => m.id === activeMainId);
  const activeMainHasSub = activeMain?.subMenu?.categories?.length > 0;

  const handleMainClick = (item) => {
    if (item.subMenu?.categories?.length) {
      setOpenSubPanelId(item.id);
      return;
    }
    if (item.link) {
      navigate(item.link.startsWith("/") ? item.link : `/${item.link}`);
      setOpenSubPanelId(null);
    }
  };

  const handleBack = () => {
    setOpenSubPanelId(null);
  };

  const handleOpenPanel = () => {
    if (activeMainId) setOpenSubPanelId(activeMainId);
  };

  const handleOpenSavedReportMenu = (event, subItem) => {
    event.preventDefault();
    event.stopPropagation();
    setSavedReportMenuAnchorEl(event.currentTarget);
    setSelectedSavedReportForAction(subItem);
  };

  const handleCloseSavedReportMenu = () => {
    setSavedReportMenuAnchorEl(null);
    setSelectedSavedReportForAction(null);
  };

  const handleDeleteSavedReport = async () => {
    if (!selectedSavedReportForAction?.reportId || !selectedSavedReportForAction?.reportKey) {
      handleCloseSavedReportMenu();
      return;
    }
    await deleteCustomReportById(
      clientId,
      selectedSavedReportForAction.reportKey,
      selectedSavedReportForAction.reportId,
      selectedPlatform
    );
    await refreshCustomSavedReports?.();
    handleCloseSavedReportMenu();
  };

  const topMenuItems = useMemo(
    () => menuConfig.filter((item) => !item.pinnedBottom),
    [menuConfig]
  );

  const bottomMenuItems = useMemo(
    () => menuConfig.filter((item) => item.pinnedBottom),
    [menuConfig]
  );

  const renderRailItem = (item) => {
    const isActive = activeMainId === item.id;
    const hasSub = item.subMenu?.categories?.length > 0;
    const isSubPanelOpen = openSubPanelId === item.id;
    const IconComponent = item.icon || SettingsRounded;

    return (
      <button
        key={item.id}
        type="button"
        className={`sidebar-rail-item ${
          isActive ? "sidebar-rail-item--active" : ""
        } ${isSubPanelOpen ? "sidebar-rail-item--subpanel-open" : ""}`}
        onClick={() => handleMainClick(item)}
        aria-expanded={hasSub && openSubPanelId === item.id}
      >
        <span style={{ position: "relative" }} className="sidebar-rail-item-inner">
          <span className="sidebar-rail-icon-wrap">
            <IconComponent
              className="sidebar-rail-icon"
              sx={{
                fontSize: 22,
              }}
            />
            {item.badge && <span className="sidebar-rail-badge" />}
          </span>
          <span
            className={`sidebar-rail-label ${
              item.label.trim().split(/\s+/).length === 1
                ? "sidebar-rail-label--single"
                : "sidebar-rail-label--multi"
            }`}
          >
            {(() => {
              const words = item.label.trim().split(/\s+/);
              if (words.length <= 1) return item.label;
              return (
                <>
                  {words[0]}
                  <br />
                  {words.slice(1).join(" ")}
                </>
              );
            })()}
          </span>
          {menuHasNewBadge(item) ? (
            <SidebarNewBadge
              variant="rail"
              label={getNewBadgeLabel(item)}
            />
          ) : null}
        </span>
      </button>
    );
  };

  return (
    <div className="sidebar-section-main-container">
      {/* LEFT RAIL */}
      <aside className="sidebar-rail">
        <Link
          to="/"
          className="sidebar-rail-logo"
          onClick={() => setOpenSubPanelId(null)}
        >
          {config && (
            <img
              src={
                config.name === "Adyogi"
                  ? `/logo/${config.logo}`
                  : `/logo/${config.logo_white}`
              }
              alt="Logo"
              className="sidebar-rail-logo-img"
            />
          )}
        </Link>

        <div className="sidebar-rail-nav-wrap">
          <nav className="sidebar-rail-nav sidebar-rail-nav--top">
            {topMenuItems.map(renderRailItem)}
          </nav>

          <nav className="sidebar-rail-nav sidebar-rail-nav--bottom">
            {bottomMenuItems.map(renderRailItem)}
          </nav>
        </div>
      </aside>

      {/* REOPEN BUTTON */}
      {!hasSubPanel && activeMainHasSub && (
        <div className="sidebar-subpanel-toggle-wrap">
          <button
            type="button"
            className="sidebar-subpanel-back"
            onClick={handleOpenPanel}
            aria-label="Open sub-menu"
          >
            <ChevronRight sx={{ fontSize: 18 }} />
          </button>
        </div>
      )}

      {/* SUB PANEL */}
      {hasSubPanel && selectedMain && (
        <aside className="sidebar-subpanel">
          <header className="sidebar-subpanel-header">
            {(() => {
              const IconComponent = selectedMain.icon || SettingsRounded;
              return (
                <IconComponent
                  className="sidebar-subpanel-header-icon"
                  sx={{ fontSize: 22 }}
                />
              );
            })()}

            <span className="sidebar-subpanel-header-title">
              {selectedMain.label}
              {selectedMain.isNew ? (
                <SidebarNewBadge
                  variant="header"
                  label={getNewBadgeLabel(selectedMain)}
                />
              ) : null}
            </span>

            <button
              type="button"
              className="sidebar-subpanel-back"
              onClick={handleBack}
              aria-label="Close sub-menu"
            >
              <ChevronLeft sx={{ fontSize: 18 }} />
            </button>
          </header>

          <div className="sidebar-subpanel-content">
            {selectedMain.subMenu.categories.map((cat) => {
              const visibleItems = cat.items.filter(
                (sub) => !sub.showForMultiuserOnly || isMultiuser
              );
              if (visibleItems.length === 0) return null;
              return (
              <div key={cat.title} className="sidebar-subpanel-category">
                <h3 className="sidebar-subpanel-category-title">
                  {cat.title}
                  {cat.count != null && ` (${cat.count})`}
                </h3>

                <ul className="sidebar-subpanel-list">
  {visibleItems.map((sub) => {
    const isSelected = activeSubId === sub.id;
    const isSavedReportsCategory = cat?.title === "Saved Reports";

    if (sub.isLocked?.(clientId)) {
      return (
        <li key={sub.id}>
          <Tooltip title={sub.lockedMessage ?? ""} placement="right" arrow>
            <div className="sidebar-subpanel-item sidebar-subpanel-item--locked" aria-disabled="true">
              <span className="sidebar-subpanel-item-content">
                <span className="sidebar-subpanel-item-name">{sub.name}</span>
                <LockOutlined className="sidebar-subpanel-lock-icon" />
              </span>
            </div>
          </Tooltip>
        </li>
      );
    }

    return (
      <li key={sub.id}>
        {sub.isExternal ? (
          //External Link (NO React Router)
          <a
            href={sub.link}
            style={{ cursor: "pointer" }}
            target="_blank"
            rel="noopener noreferrer"
            className={`sidebar-subpanel-item ${
              isSelected ? "sidebar-subpanel-item--active" : ""
            }`}
            onClick={(e) => {
              if (sub.action) {
                e.preventDefault();
                handleExternalAction(sub.action);
              }
            }}
          >
            <SubMenuItemLabel
              name={sub.name}
              isNew={sub.isNew}
              newLabel={getNewBadgeLabel(sub)}
            />
          </a>
        ) : (
          //Internal Route
          <div style={{ display: "flex", alignItems: "center", width: "100%" }}>
            <Link
              to={`/${sub.link}`}
              className={`sidebar-subpanel-item ${
                isSelected ? "sidebar-subpanel-item--active" : ""
              }`}
              style={{ flex: 1 }}
            >
              <SubMenuItemLabel
                name={sub.name}
                isNew={sub.isNew}
                newLabel={getNewBadgeLabel(sub)}
              />
            </Link>
            {isSavedReportsCategory ? (
              <IconButton
                size="small"
                onClick={(event) => handleOpenSavedReportMenu(event, sub)}
              >
                <MoreVert fontSize="small" />
              </IconButton>
            ) : null}
          </div>
        )}
      </li>
    );
  })}
</ul>

              </div>
              );
            })}
          </div>
        </aside>
      )}
      <Menu
        anchorEl={savedReportMenuAnchorEl}
        open={Boolean(savedReportMenuAnchorEl)}
        onClose={handleCloseSavedReportMenu}
      >
        <MenuItem onClick={handleDeleteSavedReport}>Delete</MenuItem>
      </Menu>
    </div>
  );
}

export default Sidebar;
